import { Modal } from 'reactstrap';
import { IoCloseOutline } from 'react-icons/io5';
import { getTranslationKey } from 'helpers/texting';
import { IMessage } from 'store/slices/messages';
import { getCtaLink } from './getCtaLink';

import styled from 'styled-components';
import media from 'assets/css/media';

const font = "var(--font, 'Noto Sans Display', sans-serif)";

const StyledModal = styled(Modal)`
  max-width: 570px;
  // Template stylesheets force 'border-radius: 0 !important' on .modal-content, hence &&&&
  &&&& .modal-content {
    position: relative;
    background-color: #ffffff;
    border: none !important;
    border-radius: 30px !important;
    padding: 40px;
  }

  @media all and (max-width: ${media.mobile}) {
    max-width: min(450px, calc(100% - 20px));
    margin: 0 auto;

    &&&& .modal-content {
      padding: 25px;
    }
  }
`;

const Content = styled.div`
  max-height: 80vh;
  overflow-y: auto;
`;

const Title = styled.h2`
  &&&& {
    font-family: ${font} !important;
    font-size: 31px !important;
    font-weight: 400 !important;
    line-height: 39px !important;
    color: #000000 !important;
    margin: 0 0 20px;

    @media all and (max-width: ${media.mobile}) {
      font-size: 25px !important;
      line-height: 32px !important;
    }
  }
`;

const Text = styled.p`
  font-family: ${font} !important;
  font-size: 16px;
  font-weight: 400;
  line-height: 27px;
  color: #000000;
  white-space: pre-line;
  margin: 0 0 35px;
`;

const Image = styled.img`
  display: block;
  width: 100%;
  border-radius: 12px;
  margin-bottom: 25px;
`;

const CtaButton = styled.a`
  font-family: ${font} !important;
  font-size: 16px;
  font-weight: 400;
  width: 100%;
  max-width: 290px;
  height: 47px;
  border-radius: 24px;
  background: #000000;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
  text-align: center;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    color: #ffffff;
    text-decoration: none;
    opacity: 0.85;
  }
`;

// Sits outside <Content> — its overflow-y would clip an absolutely positioned child
const CloseButton = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  z-index: 1;
  cursor: pointer;
  color: #8c8c8c;
  line-height: 0;

  svg {
    width: 25px;
    height: 25px;
  }
`;

interface IProps {
  message: IMessage;
  isOpened: boolean;
  toggle: () => void;
}

export const NewMessage: React.FC<IProps> = ({ message, isOpened, toggle }) => {
  const { url, target } = getCtaLink(message.cta);

  return (
    <StyledModal
      isOpen={isOpened}
      toggle={toggle}
      size="md"
      centered
      zIndex={999999}
      data-cy="new-message-modal"
    >
      <CloseButton onClick={toggle} data-cy="new-message-modal-close">
        <IoCloseOutline />
      </CloseButton>

      <Content>
        {message.title && <Title data-cy="new-message-modal-title">{message.title}</Title>}

        <Text data-cy="new-message-modal-text">{message.message}</Text>

        {message.publicImageKey && (
          <Image
            // @ts-ignore
            src={`${window.CDN_URL}/public/${message.publicImageKey}`}
            alt=""
            data-cy="new-message-modal-image"
          />
        )}

        {message.cta && (
          <CtaButton href={url} target={target} data-cy="new-message-modal-cta">
            {message.cta.text || getTranslationKey('ctaPreviewPlaceholder')}
          </CtaButton>
        )}
      </Content>
    </StyledModal>
  );
};
